import React, { createContext, useContext, useState, useCallback } from 'react';
import { loadSubscriptions, saveSubscriptions } from '../utils/storage';

const NotificationsContext = createContext(null);

// Shape: { [citySlug]: { [topicId]: { topicId, citySlug, methods: [], contact: {}, address: '', subscribedAt } } }

export function NotificationsProvider({ children }) {
  const [subscriptions, setSubscriptions] = useState(loadSubscriptions);

  const getSubscriptionsForCity = useCallback(
    (citySlug) => Object.values(subscriptions[citySlug] || {}),
    [subscriptions]
  );

  const isSubscribed = useCallback(
    (citySlug, topicId) => !!(subscriptions[citySlug] && subscriptions[citySlug][topicId]),
    [subscriptions]
  );

  const subscribe = useCallback((citySlug, topicId, { methods, contact, address }) => {
    setSubscriptions((prev) => {
      const next = {
        ...prev,
        [citySlug]: {
          ...(prev[citySlug] || {}),
          [topicId]: {
            topicId,
            citySlug,
            methods,
            contact,
            address: address || '',
            subscribedAt: new Date().toISOString(),
          },
        },
      };
      saveSubscriptions(next);
      return next;
    });
  }, []);

  const unsubscribe = useCallback((citySlug, topicId) => {
    setSubscriptions((prev) => {
      const cityData = { ...(prev[citySlug] || {}) };
      delete cityData[topicId];
      const next = { ...prev, [citySlug]: cityData };
      saveSubscriptions(next);
      return next;
    });
  }, []);

  const totalCount = Object.values(subscriptions).reduce(
    (sum, cityMap) => sum + Object.keys(cityMap).length,
    0
  );

  return (
    <NotificationsContext.Provider
      value={{ subscriptions, getSubscriptionsForCity, isSubscribed, subscribe, unsubscribe, totalCount }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsContext() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotificationsContext must be used inside NotificationsProvider');
  return ctx;
}
